import { useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSettings } from "@/context/SettingsContext";
import { assetUrl } from "@/lib/api";

const PAGE_KEYS = { "/": "home", "/services": "services", "/events": "events", "/contact": "contact" };

export const HeadManager = () => {
  const { settings } = useSettings();
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    if (!settings) return;
    const { brand = {}, seo = {}, contact = {}, social = [], images = {} } = settings;
    const page = (seo.pages || {})[PAGE_KEYS[pathname]] || {};
    const siteUrl = (seo.site_url || "").replace(/\/+$/, "");
    const canonical = siteUrl ? `${siteUrl}${pathname}` : "";
    const title = page.title || (brand.company_name ? `${brand.company_name} — ${brand.tagline || ""}`.replace(/ — $/, "") : "Cyber Planet");
    const description = page.description || (seo.pages || {}).home?.description || "";

    document.querySelectorAll("[data-cp-dyn]").forEach((n) => n.remove());

    document.title = title;

    let link = document.querySelector("link[rel='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = brand.favicon_url ? assetUrl(brand.favicon_url) : `${process.env.PUBLIC_URL || ""}/favicon.svg`;

    const setMeta = (attr, name, content) => {
      if (!content) return;
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
      el.setAttribute("data-cp-dyn", "1");
    };

    setMeta("name", "description", description);
    setMeta("name", "robots", "index, follow, max-image-preview:large, max-snippet:-1");
    if (canonical) {
      let cl = document.head.querySelector("link[rel='canonical']");
      if (!cl) {
        cl = document.createElement("link");
        cl.rel = "canonical";
        document.head.appendChild(cl);
      }
      cl.setAttribute("href", canonical);
      cl.setAttribute("data-cp-dyn", "1");
    }
    const ogImage = seo.og_image
      ? seo.og_image.startsWith("/") && siteUrl
        ? `${siteUrl}${seo.og_image}`
        : seo.og_image
      : images.hero || "";
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:image", ogImage);
    setMeta("property", "og:site_name", brand.company_name || "Cyber Planet");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", ogImage);

    const injectRaw = (html) => {
      if (!html || !html.includes("<")) return;
      const wrap = document.createElement("div");
      wrap.innerHTML = html;
      Array.from(wrap.childNodes).forEach((n) => {
        if (n.nodeType !== 1) return;
        let el = n;
        if (el.tagName === "SCRIPT") {
          const s = document.createElement("script");
          Array.from(el.attributes).forEach((a) => s.setAttribute(a.name, a.value));
          s.text = el.text;
          el = s;
        }
        el.setAttribute("data-cp-dyn", "1");
        document.head.appendChild(el);
      });
    };

    const ga = (seo.ga_id || "").trim();
    if (ga) {
      if (ga.includes("<")) {
        injectRaw(ga);
      } else if (/^(G-|UA-|GTM-)/i.test(ga)) {
        const s = document.createElement("script");
        s.async = true;
        s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ga)}`;
        s.setAttribute("data-cp-dyn", "1");
        const cfg = document.createElement("script");
        cfg.setAttribute("data-cp-dyn", "1");
        cfg.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${ga}');`;
        document.head.appendChild(s);
        document.head.appendChild(cfg);
      }
    }

    const gsc = (seo.gsc_verification || "").trim();
    if (gsc) {
      if (gsc.includes("<")) {
        injectRaw(gsc);
      } else {
        const meta = document.createElement("meta");
        meta.name = "google-site-verification";
        meta.content = gsc;
        meta.setAttribute("data-cp-dyn", "1");
        document.head.appendChild(meta);
      }
    }

    const org = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: brand.company_name || "Cyber Planet Inc",
      ...(brand.tagline ? { slogan: brand.tagline } : {}),
      ...(description ? { description } : {}),
      ...(siteUrl ? { url: siteUrl } : {}),
      ...(ogImage ? { image: ogImage } : {}),
      ...(contact.email ? { email: contact.email } : {}),
      ...(contact.phone ? { telephone: contact.phone } : {}),
    };
    const socialUrls = (social || []).filter((s) => s.url).map((s) => s.url);
    if (socialUrls.length) org.sameAs = socialUrls;
    let ld = document.getElementById("cp-jsonld-organization");
    if (!ld) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.id = "cp-jsonld-organization";
      document.head.appendChild(ld);
    }
    ld.setAttribute("data-cp-dyn", "1");
    ld.textContent = JSON.stringify(org);

    injectRaw(seo.header_script);

    if (seo.body_script && seo.body_script.includes("<")) {
      const holder = document.createElement("div");
      holder.setAttribute("data-cp-dyn", "1");
      holder.style.display = "contents";
      document.body.prepend(holder);
      const wrap = document.createElement("div");
      wrap.innerHTML = seo.body_script;
      Array.from(wrap.childNodes).forEach((n) => {
        if (n.nodeType !== 1) return;
        let el = n;
        if (el.tagName === "SCRIPT") {
          const s = document.createElement("script");
          Array.from(el.attributes).forEach((a) => s.setAttribute(a.name, a.value));
          s.text = el.text;
          el = s;
        }
        el.setAttribute("data-cp-dyn", "1");
        holder.appendChild(el);
      });
    }

    if (seo.footer_script && seo.footer_script.includes("<")) {
      const holder = document.createElement("div");
      holder.setAttribute("data-cp-dyn", "1");
      holder.style.display = "contents";
      document.body.appendChild(holder);
      const wrap = document.createElement("div");
      wrap.innerHTML = seo.footer_script;
      Array.from(wrap.childNodes).forEach((n) => {
        if (n.nodeType !== 1) return;
        let el = n;
        if (el.tagName === "SCRIPT") {
          const s = document.createElement("script");
          Array.from(el.attributes).forEach((a) => s.setAttribute(a.name, a.value));
          s.text = el.text;
          el = s;
        }
        el.setAttribute("data-cp-dyn", "1");
        holder.appendChild(el);
      });
    }
  }, [settings, pathname]);

  return null;
};
